import { memo } from "react";

import { CalendarIcon, ClockIcon } from "./icons";

/**
 * NotesSection: 留意事項（チェックイン/アウト + ポリシーカード）
 * ゲスト側 app/frontend/guest/_shared/src/components/facility-info/NotesSection.astro の逐語再現。
 */

type CancelPolicyItem = { beforeDaysText: string; chargeRatioText: string };
type KidsPolicyItem = { ageRangeText: string; discountDetailText: string };

type Props = {
	checkinText?: string;
	checkoutText?: string;
	cancelPolicy?: CancelPolicyItem[];
	kidsPolicy?: KidsPolicyItem[];
	kidsCosleepingPolicy?: KidsPolicyItem;
};

const DEFAULT_CANCEL: CancelPolicyItem[] = [
	{ beforeDaysText: "7 日前まで", chargeRatioText: "無料" },
	{ beforeDaysText: "3 日前まで", chargeRatioText: "50%" },
	{ beforeDaysText: "当日", chargeRatioText: "100%" },
];

const DEFAULT_KIDS: KidsPolicyItem[] = [
	{ ageRangeText: "12 歳以上", discountDetailText: "大人料金" },
	{ ageRangeText: "6〜11 歳", discountDetailText: "50% 割引" },
];

export const NotesSection = memo(
	({
		checkinText = "15:00 〜 21:00",
		checkoutText = "〜 11:00",
		cancelPolicy = DEFAULT_CANCEL,
		kidsPolicy = DEFAULT_KIDS,
		kidsCosleepingPolicy,
	}: Props) => {
		return (
			<div>
				<h3 className="mb-5 text-xl font-semibold text-zinc-800">留意事項</h3>

				<div className="flex flex-col gap-4">
					{/* チェックイン・アウト */}
					<div className="flex flex-col gap-3">
						<div className="flex items-center gap-2">
							<ClockIcon size={24} className="text-zinc-800" />
							<p className="text-base font-semibold text-zinc-800">チェックイン / アウト</p>
						</div>
						<div className="flex gap-10">
							<div className="flex flex-col gap-1">
								<p className="text-sm font-semibold text-zinc-800">チェックイン</p>
								<p className="text-base text-zinc-800">{checkinText}</p>
							</div>
							<div className="flex flex-col gap-1">
								<p className="text-sm font-semibold text-zinc-800">チェックアウト</p>
								<p className="text-base text-zinc-800">{checkoutText}</p>
							</div>
						</div>
					</div>

					{/* ポリシーカード */}
					<div className="flex flex-col gap-3 rounded-xl bg-zinc-50 p-4">
						<div className="flex items-center gap-2">
							<CalendarIcon size={24} className="text-zinc-800" />
							<p className="text-base font-semibold text-zinc-800">ポリシー</p>
						</div>
						<div className="flex flex-col gap-4">
							{/*
							 * preview コンテナは幅 420px 固定のため、viewport ベースの laptop:
							 * breakpoint は発火しないが、Tailwind media query はビューポート
							 * 幅で判定するため PC ブラウザでは発火してしまい、狭いプレビュー内で
							 * キャンセル/キッズが無理やり横並びになって崩れる。ゲスト narrow 表示
							 * の再現として、preview 内では常に flex-col を維持する。
							 */}
							<div className="flex flex-col gap-4">
								{/* キャンセルポリシー */}
								<div className="flex flex-1 flex-col gap-1">
									<p className="text-sm font-semibold text-zinc-800">キャンセルポリシー</p>
									<div className="grid-cols-label-value grid">
										{cancelPolicy.slice(0, 3).map((policy, idx) => (
											<div key={idx} className="contents">
												<span className="text-sm text-zinc-500">{policy.beforeDaysText}</span>
												<span className="text-sm text-zinc-500">{policy.chargeRatioText}</span>
											</div>
										))}
										{cancelPolicy.length > 3 && (
											<span className="col-span-2 text-sm text-zinc-500">…</span>
										)}
									</div>
								</div>

								{/* キッズポリシー + 添い寝ポリシー */}
								{(kidsPolicy.length > 0 || kidsCosleepingPolicy) && (
									<div className="flex flex-1 flex-col gap-1">
										<p className="text-sm font-semibold text-zinc-800">キッズポリシー</p>
										{kidsPolicy.length > 0 && (
											<div className="grid-cols-label-value grid">
												{kidsPolicy.slice(0, 3).map((vm, idx) => (
													<div key={idx} className="contents">
														<span className="text-sm text-zinc-500">{vm.ageRangeText}</span>
														<span className="text-sm text-zinc-500">
															：{vm.discountDetailText}
														</span>
													</div>
												))}
												{kidsPolicy.length > 3 && (
													<span className="col-span-2 text-sm text-zinc-500">…</span>
												)}
											</div>
										)}
										{kidsCosleepingPolicy && (
											<div className="mt-2">
												<p className="text-xs font-medium text-zinc-400">添い寝ポリシー</p>
												<div className="grid-cols-label-value grid">
													<span className="text-sm text-zinc-500">
														{kidsCosleepingPolicy.ageRangeText}
													</span>
													<span className="text-sm text-zinc-500">
														：{kidsCosleepingPolicy.discountDetailText}
													</span>
												</div>
											</div>
										)}
									</div>
								)}
							</div>

							<button
								type="button"
								className="rounded-lg w-fit border border-zinc-300 bg-white px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
							>
								ポリシーを見る
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	},
);

NotesSection.displayName = "NotesSection";
